import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useMessages from '../../../hooks/conversations/useMessages';
import useUserInfo from '../../../hooks/useUserInfo';
import useEmployeeInfo from '../../../hooks/useEmployeeInfo';
import useRestaurantInfo from '../../../hooks/useRestaurantInfo';
import useFetchJobOffer from '../../../hooks/jobs/useFetchJobOffer';
import useOtherUserInfo from '../../../hooks/chat/useOtherUserInfo';
import useChatSocket from '../../../hooks/chat/useChatSocket';
import useSendMessage from '../../../hooks/chat/useSendMessage';
import getProfileImageUrl from '../../../utils/chat/getProfileImageUrl';
import useHire from '../../../hooks/hiring/useHire';
import useAcceptHiring from '../../../hooks/hiring/useAcceptHiring';
import useRejectHiring from '../../../hooks/hiring/useRejectHiring';
import useRefreshHiringData from '../../../hooks/hiring/useRefreshHiringData';
import useFetchHiringData from '../../../hooks/hiring/useFetchHiringData';
import useUpdateHiring from '../../../hooks/hiring/useUpdateHiring';
import useFetchHiringReviews from '../../../hooks/reviews/useFetchHiringReviews';
import useCheckUserReview from '../../../hooks/reviews/useCheckUserReview';
import useReviewSubmission from '../../../hooks/reviews/useReviewSubmission';
import useFetchConversationData from '../../../hooks/conversations/useFetchConversationData';
import useMarkNotificationsReadByConversation from '../../../hooks/notifications/useMarkNotificationsReadByConversation';
import useFetchScheduledCalls from '../../../hooks/calls/useFetchScheduledCalls';
import Loading from '../../Shared/Loading/Loading';
import ReviewModal from './ReviewModal/ReviewModal';
import HiringOffer from './HiringOffer';
import ScheduleCallModal from './ScheduleCallModal/ScheduleCallModal';
import ChatHeader from './Chat/ChatHeader';
import ScheduledCallsDisplay from './Chat/ScheduledCallsDisplay';
import ChatInput from './Chat/ChatInput';
import ChatMessages from './Chat/ChatMessages';
import ReviewCompletionBanner from './Chat/ReviewCompletionBanner';

import './chat.css';

const Chat = ({ userType }) => {
  const [message, setMessage] = useState('');
  const [isHiring, setIsHiring] = useState(null);
  const [hiringModalMode, setHiringModalMode] = useState(null); // 'create' or 'update'
  const { conversationId, employeeId, restaurantUserId } = useParams();
  const [messages, setMessages] = useMessages(conversationId);
  const { userInfo, error, loading } = useUserInfo();
  
  // Use the correct hook based on user type
  const isEmployee = userType === 'profesionales';
  const { employeeInfo, loading: employeeLoading } = useEmployeeInfo(isEmployee ? null : employeeId);
  
  const { conversationData, setConversationData, loading: conversationLoading } = useFetchConversationData(conversationId);
  const { hiringData, setHiringData } = useFetchHiringData(conversationId);
  const { reviews: hiringReviews, loading: reviewsLoading, refreshReviews } = useFetchHiringReviews(hiringData?.id);
  const { hasReviewed: userHasReviewed, loading: checkReviewLoading, refreshCheck: refreshUserReviewCheck } = useCheckUserReview(hiringData?.id, userType);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewType, setReviewType] = useState(null);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [callToEdit, setCallToEdit] = useState(null);
  
  const { scheduledCalls, setScheduledCalls, loading: scheduledCallsLoading } = useFetchScheduledCalls(conversationId);

  useMarkNotificationsReadByConversation(conversationId);
  
  const { jobOffer, loading: jobLoading } = useFetchJobOffer(
    conversationData?.jobOfferId ? conversationData.jobOfferId : null
  );
  
  const { restaurantInfo, loading: restaurantLoading } = useRestaurantInfo(
    restaurantUserId
  );
  
  // Get the info and loading state based on user type and conversation type
  const conversationType = conversationData?.type;
  const isApplicantConversation = conversationType === 'applicant';
  
  const { otherUserInfo, otherUserLoading } = useOtherUserInfo({
    isEmployee,
    conversationType,
    jobOffer,
    jobLoading,
    restaurantInfo,
    restaurantLoading,
    employeeInfo,
    employeeLoading,
    conversationLoading,
    restaurantUserId,
    employeeId
  });

  // Get the profile image URL
  const profileImageUrl = getProfileImageUrl({
    otherUserInfo,
    isEmployee,
    isApplicantConversation
  });

  // Initialize chat socket
  const { socket, connectionStatus, tokenError } = useChatSocket({
    conversationId,
    userInfo,
    loading,
    otherUserLoading
  });

  // Handle sending messages
  const sendMessageHandler = useSendMessage({
    socket,
    connectionStatus,
    userInfo,
    userType,
    employeeId,
    restaurantUserId,
    conversationId,
    setMessages
  });

  const handleSendMessage = () => {
    if (sendMessageHandler(message)) {
      setMessage('');
    }
  };

  // Listen for incoming messages from socket
  useEffect(() => {
    if (!socket || connectionStatus !== 'connected') {
      return;
    }

    const handleNewMessage = (newMessage) => {
      setMessages((prev) => {
        // Check if message already exists to avoid duplicates
        const messageExists = prev.some(msg => 
          msg.id === newMessage.id || 
          (msg.text === newMessage.text && 
           msg.createdAt === newMessage.createdAt)
        );
        
        if (messageExists) {
          return prev;
        }
        
        return [...prev, newMessage];
      });
    };

    // Listen for new messages
    socket.on('newMessage', handleNewMessage);
    socket.on('message', handleNewMessage);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('message', handleNewMessage);
    };
  }, [socket, connectionStatus, setMessages]);

  const refreshAfterHire = useRefreshHiringData({
    conversationId,
    setMessages,
    setHiringData
  });

  const handleReviewSubmitted = useReviewSubmission({
    refreshAfterHire,
    refreshReviews,
    refreshUserReviewCheck
  });

  const onHireClick = () => {
    setHiringModalMode('create');
    setIsHiring(true);
  }

  const onUpdateHiringClick = () => {
    if (!hiringData || !hiringData.id) {
      console.error('Cannot update: hiringData or hiringData.id is missing', { hiringData });
      alert('No se puede actualizar: falta la información de la contratación');
      return;
    }
    setHiringModalMode('update');
    setIsHiring(true);
  }

  const { handleAccept, isAccepting: isAcceptingHiring } = useAcceptHiring({
    hiringId: hiringData?.id,
    startDate: jobOffer?.startDate,
    endDate: jobOffer?.endDate,
    onSuccess: refreshAfterHire,
    onError: (error) => alert(`Error: ${error}`)
  });

  const { handleReject, isRejecting: isRejectingHiring } = useRejectHiring({
    hiringId: hiringData?.id,
    onSuccess: refreshAfterHire,
    onError: (error) => alert(`Error: ${error}`)
  });

  const handleAcceptHiring = useCallback(async () => {
    if (!hiringData?.id || isAcceptingHiring) return;
    await handleAccept();
  }, [hiringData?.id, isAcceptingHiring, handleAccept]);

  const handleRejectHiring = useCallback(async () => {
    if (!hiringData?.id || isRejectingHiring) return;
    await handleReject();
  }, [hiringData?.id, isRejectingHiring, handleReject]);

  const processingHiring = isAcceptingHiring || isRejectingHiring;

  // Agent features removed

  if (loading) {
    return <Loading text="chat" />;
  }
  
  if (error) {
    return (
      <div className="chat__container">
        <div className="chat__error">
          <h3>Error loading chat</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat__container">
      <ChatHeader
        otherUserInfo={otherUserInfo}
        otherUserLoading={otherUserLoading}
        profileImageUrl={profileImageUrl}
        isEmployee={isEmployee}
        isApplicantConversation={isApplicantConversation}
        conversationData={conversationData}
        restaurantUserId={restaurantUserId}
        employeeId={employeeId}
        conversationId={conversationId}
        connectionStatus={connectionStatus}
        tokenError={tokenError}
        onScheduleCallClick={() => {
          setCallToEdit(null);
          setShowScheduleCallModal(true);
        }}
        onHireClick={onHireClick}
        hiringData={hiringData}
      />

      <ScheduledCallsDisplay 
        scheduledCalls={scheduledCalls}
        onCallClick={(call) => {
          setCallToEdit(call);
          setShowScheduleCallModal(true);
        }}
      />

      {/* Messages display */}
      <ChatMessages 
        messages={messages} 
        userInfo={userInfo}
        userType={userType}
        hiringData={hiringData}
        onAcceptHiring={handleAcceptHiring}
        onRejectHiring={handleRejectHiring}
        isProcessingHiring={processingHiring}
      />

      {/* Show review button if job is completed */}
      {hiringData && hiringData.status === 'completed' && !reviewsLoading && !checkReviewLoading && (
        <ReviewCompletionBanner
          userHasReviewed={userHasReviewed}
          isEmployee={isEmployee}
          onReviewClick={() => {
                    setReviewType(isEmployee ? 'restaurant_review' : 'employee_review');
                    setShowReviewModal(true);
                  }}
        />
      )}
    
      {/* Hiring Offer Modal - handles both create and update modes */}
      {isHiring && (
        <HiringOffer 
          isOpen={isHiring}
          onClose={() => {
            setIsHiring(false);
            setHiringModalMode(null);
          }}
          isUpdateMode={hiringModalMode === 'update'}
          conversationId={conversationId}
          employeeId={employeeId}
          conversationData={conversationData}
          otherUserInfo={otherUserInfo}
          hiringId={hiringModalMode === 'update' ? (hiringData?.id || null) : null}
          currentStartDate={hiringModalMode === 'update' && hiringData?.startDate ? hiringData.startDate : null}
          currentEndDate={hiringModalMode === 'update' && hiringData?.endDate ? hiringData.endDate : null}
          onSuccess={refreshAfterHire}
        />
      )}
    
      <ChatInput
        message={message}
        setMessage={setMessage}
        handleSendMessage={handleSendMessage}
        connectionStatus={connectionStatus}
      />

      {/* Review Modal */}
      {hiringData && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setReviewType(null);
          }}
          hiringId={hiringData.id}
          reviewType={reviewType}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}

      {/* Schedule Call Modal */}
      <ScheduleCallModal
        isOpen={showScheduleCallModal}
        onClose={() => {
          setShowScheduleCallModal(false);
          setCallToEdit(null);
        }}
        conversationId={conversationId}
        initialCall={callToEdit}
        onCallScheduled={(newCall) => {
          setScheduledCalls(prev => [...prev, newCall]);
        }}
        onCallUpdated={(updatedCall) => {
          setScheduledCalls(prev => prev.map((c) => (c.id === updatedCall.id ? updatedCall : c)));
        }}
      />
    </div>
  );
};

export default Chat;